import { useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import {
  ChevronLeftIcon,
  DatableColoredIcon,
  EndPointDiamondIcon,
  Hierarchy1Icon,
  ManagesIcon,
  SettingsBoltNutIcon,
  StartPointDiamondIcon,
  TelemetrysIcon,
  TracesIcon,
  UserIcon,
  UsersIcon,
} from '@ui/components/icons'
import { NavigationLink } from '@ui/interfaces/navigation'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useAuth } from '@ui/providers/AuthProvider'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import clsx from 'clsx'

import { SideBarContent } from './components/SideBarContent'
import { InnerMenu } from './InnerMenu'

export const SideBar = ({ isDropdownMenu = false }: { isDropdownMenu?: boolean }) => {
  const location = useLocation()
  const { navigateTo } = useAppNavigation()
  const auth = useAuth()

  const { currentMember, isDemoMode } = auth

  const isCurrent = (path: string) => location.pathname.match(path)

  const isPipelinePath = useMemo(() => {
    return /pipelines\/(new|\w+)/.test(location.pathname)
  }, [location.pathname])

  const isQueryRailsLive = useFeatureFlag(FeatureFlagName.QueryRailsLive)

  const [expanded, setExpanded] = useState(false)

  const appLinks: NavigationLink[] = [
    {
      name: 'Explore',
      onClick: () => navigateTo(RouteNamesEnum.EVENTS_EXPLORER),
      current: isCurrent(`/explore/events`),
      icon: TelemetrysIcon,
    },
    {
      name: 'Traces',
      onClick: () => navigateTo(RouteNamesEnum.TRACES_EXPLORER),
      icon: TracesIcon,
      current: isCurrent(`/explore/traces`),
    },
    {
      name: 'Pipelines',
      onClick: () => navigateTo(RouteNamesEnum.ALL_PIPELINES),
      icon: Hierarchy1Icon,
      current: isCurrent(`/pipelines`),
    },
    {
      name: 'Sources',
      onClick: () => navigateTo(RouteNamesEnum.SOURCES),
      icon: StartPointDiamondIcon,
      current: isCurrent(`/sources`),
    },
    {
      name: 'Destinations',
      onClick: () => navigateTo(RouteNamesEnum.DESTINATIONS),
      icon: EndPointDiamondIcon,
      current: isCurrent(`/destinations`),
    },
  ]

  const manageLinks: NavigationLink[] = []

  if (currentMember && currentMember.is_datable_admin) {
    manageLinks.push({
      name: 'Admin',
      onClick: () => navigateTo(RouteNamesEnum.ADMIN),
      icon: SettingsBoltNutIcon,
      current: isCurrent('/admin'),
    })
  }

  if (!isDemoMode && !isQueryRailsLive) {
    manageLinks.push({
      name: 'Manage',
      onClick: () => navigateTo(RouteNamesEnum.MANAGE_MEMBERS, { orgId: auth.currentOrganization!.id }),
      icon: ManagesIcon,
      current: isCurrent('/members') || isCurrent('/instances$') || isCurrent('/ingress'),
    })
  }

  if (isQueryRailsLive) {
    manageLinks.push({
      name: 'Settings',
      onClick: () => navigateTo(RouteNamesEnum.SETTINGS_USERS),
      icon: SettingsBoltNutIcon,
      current: isCurrent('/settings'),
    })
  }

  const innerMenuLinks: NavigationLink[] = []
  if (isCurrent('/settings')) {
    innerMenuLinks.push({
      name: 'Organization',
      onClick: () => {},
      type: 'header',
      icon: UsersIcon,
      current: null,
    })
    innerMenuLinks.push({
      name: 'Users',
      onClick: () => navigateTo(RouteNamesEnum.SETTINGS_USERS),
      icon: UserIcon,
      current: isCurrent('/settings/users'),
    })
    innerMenuLinks.push({
      name: 'Teams',
      onClick: () => navigateTo(RouteNamesEnum.SETTINGS_TEAMS),
      icon: UsersIcon,
      current: isCurrent('/settings/teams'),
    })
  }

  return (
    <div className="flex flex-row">
      <div
        className={clsx(
          'relative z-40 flex  h-full flex-col space-y-2  border border-gray-200 bg-white transition-all duration-300',
          isDropdownMenu ? 'rounded-md' : 'border-r border-r-gray-200',
          {
            'min-w-48 w-48': isPipelinePath || expanded,
            'min-w-14 w-14': !isPipelinePath && !expanded,
          }
        )}>
        {!isDropdownMenu && (
          <div
            onClick={() => setExpanded(!expanded)}
            className="size-7 absolute -right-2 top-14 flex cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white p-1 transition-all duration-300 hover:bg-gray-100">
            <ChevronLeftIcon
              className={clsx('size-6 fill-none stroke-gray-500', expanded ? 'rotate-0' : 'rotate-180')}
            />
          </div>
        )}

        {!isDropdownMenu && (
          <div className="flex h-16 w-full items-center justify-center">
            {expanded ? (
              <img className="max-w-32 px-4" src="/datable-logo.svg" alt="Datable" />
            ) : (
              <DatableColoredIcon className="size-6" />
            )}
          </div>
        )}

        <div className="h-full">
          <SideBarContent links={appLinks} manageLinks={manageLinks} expanded={isPipelinePath || expanded} />
        </div>
      </div>

      {innerMenuLinks.length > 0 && <InnerMenu links={innerMenuLinks} />}
    </div>
  )
}
